import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import { useTranslation } from 'react-i18next';

HighchartsMore(Highcharts);
SolidGauge(Highcharts);

interface LivisDonutProgressChartProps {
    percentage: number;
    label?: string;
    height?: number;
    width?: number;
    color?: string;
    backgroundColor?: string;
    total?: number;
    labelled?: number;
    qa?: number;
    noDataMessage?: string;
    noDataMessageSubtitle?: string;
}

const LivisDonutProgressChart: React.FC<LivisDonutProgressChartProps> = ({
    percentage,
    label = "LABELED",
    height = 300,
    width = 220,
    color = 'var(--livis-accent-color)',
    backgroundColor = '#E8EAF6',
    qa,
    noDataMessage = 'No Data to Display',
    noDataMessageSubtitle =""
}) => {
    const { t } = useTranslation();
    const hasData = percentage !== undefined && percentage !== null && percentage > 0;

    const qaValue = qa !== undefined ? qa : (percentage <= 90 ? 0.1 * (100 - percentage) : 100 - percentage);
    const [centerValue, setCenterValue] = React.useState<number>(percentage);
    const [centerLabel, setCenterLabel] = React.useState<string>(label);

    React.useEffect(() => {
        setCenterValue(percentage);
        setCenterLabel(label);
    }, [percentage, label]);

    try { console.debug('LivisDonutProgressChart', { percentage, qa, qaValue, centerValue }); } catch (e) { void e; }

    if (!hasData) {
        return (
            <div
        style={{
          width: '100%',
          minHeight: '230px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <p style={{ color: 'var(--livis-color-primary-gray)', fontSize: "1.2rem", textAlign: "center", margin: 0, fontWeight: 500 }}>
          {t(noDataMessage)}
        </p>
        {noDataMessageSubtitle && (
          <p style={{ color: 'var(--livis-color-primary-gray)', fontSize: "1rem", textAlign: "center", margin: 0, opacity: 0.8 }}>
            {t(noDataMessageSubtitle)}
          </p>
        )}
      </div>
        );
    }

    const options: Highcharts.Options = {
        chart: {
            type: 'solidgauge',
            backgroundColor: 'transparent',
            height: height,
            width: width,
            // Animation is disabled here because callers that update `percentage`
            // rapidly (e.g. a client-side eased progress ring) already animate
            // the value smoothly themselves — Highcharts' own ~1s point-update
            // animation would otherwise chase a constantly-moving target and
            // visibly lag behind the (unanimated) center-value text.
            animation: false,
        },
        title: { text: undefined },
        pane: {
            center: ['50%', '50%'],
            size: '100%',
            startAngle: 0,
            endAngle: 360,
            background: [{
                backgroundColor: backgroundColor,
                innerRadius: '70%',
                outerRadius: '100%',
                shape: 'arc'
            }]
        },
        yAxis: {
            min: 0,
            max: 100,
            lineWidth: 0,
            tickWidth: 0,
            minorTickInterval: undefined,
            tickAmount: 0,
            labels: { enabled: false }
        },
        plotOptions: {
            solidgauge: {
                dataLabels: { enabled: false },
                linecap: 'round',
                stickyTracking: false,
                rounded: true,
                animation: false,
            },
            series: {
                animation: false,
            },
        },
        series: (
            qaValue && qaValue > 0
        ) ? [
            {
                type: 'solidgauge',
                name: 'QA',
                data: [
                    {
                        color: 'var(--livis-modal-freqency-indicator-low)',
                        radius: '100%',
                        innerRadius: '70%',
                        y: Math.min(100, percentage + qaValue),
                        events: {
                            mouseOver: function () {
                                try { setCenterValue(Math.round(qaValue)); setCenterLabel('QA'); } catch (e) { /* ignore */ }
                            },
                            mouseOut: function () {
                                try { setCenterValue(percentage); setCenterLabel(label); } catch (e) { /* ignore */ }
                            }
                        }
                    }
                ],
                enableMouseTracking: true
            },
            {
                type: 'solidgauge',
                name: 'Progress',
                data: [
                    {
                        color: color,
                        radius: '100%',
                        innerRadius: '70%',
                        y: percentage,
                        events: {
                            mouseOver: function () {
                                try { setCenterValue(Math.round(percentage)); setCenterLabel(label); } catch (e) { /* ignore */ }
                            },
                            mouseOut: function () {
                                try { setCenterValue(percentage); setCenterLabel(label); } catch (e) { /* ignore */ }
                            }
                        }
                    }
                ]
            }
        ] : [
            {
                type: 'solidgauge',
                name: 'Progress',
                data: [
                    {
                        color: color,
                        radius: '100%',
                        innerRadius: '70%',
                        y: percentage,
                        events: {
                            mouseOver: function () {
                                try { setCenterValue(Math.round(percentage)); setCenterLabel(label); } catch (e) { /* ignore */ }
                            },
                            mouseOut: function () {
                                try { setCenterValue(percentage); setCenterLabel(label); } catch (e) { /* ignore */ }
                            }
                        }
                    }
                ]
            }
        ],
        credits: { enabled: false },
        tooltip: {
            enabled: true,
            useHTML: true,
            backgroundColor: 'var(--livis-white-color)',
            borderWidth: 0,
            borderRadius: 10,
            shadow: true,
            formatter: function () {
                const point = this as any;
                const seriesName = (point.series && point.series.name) ? point.series.name : '';
                const displayLabel = seriesName === 'QA' ? 'QA' : label;
                const displayValue = seriesName === 'QA' ? centerValue : percentage;
                return `<div style="position: relative; z-index: 10; text-align: center; padding: 8px 12px;">
                            <span style="font-size: 14px; font-weight: 600; color: var(--livis-color-primary-gray);">
                                ${Math.round(displayValue)}% ${displayLabel}
                            </span>
                        </div>`;
            }
        }
    };

    return (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: '1', height: height, width: width }}>
            <HighchartsReact highcharts={Highcharts} options={options} />
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 20,
            }}>
                <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: 'var(--livis-color-primary-gray)',
                    lineHeight: '1'
                }}>
                    {centerValue}%
                </div>
                <div style={{
                    fontSize: '11px',
                    color: 'var(--livis-gray-font-color)',
                    marginTop: '4px',
                    letterSpacing: '1px',
                    fontWeight: '500'
                }}>
                    {centerLabel}
                </div>
            </div>
        </div>
    );
};

export default LivisDonutProgressChart;